import * as Sentry from '@sentry/vue';
import type {App} from 'vue';
import {type ErrorEvent} from '@sentry/types';
import {dataLayerSentrySettings} from './src/utils/dataLayerHelper';

export type SentryPluginConfig = {
	project: string;
	authToken: string;
};

export const getSentryPluginOptions = (brand: string, config: SentryPluginConfig, buildDir: string) => {
	const options = {
		org: 'eleven',
		project: config.project,
		release: `dfn-${process.env.VITE_RELEASE_VERSION}`,
		url: process.env.SENTRY_BASE_URL,
		include: buildDir,
		urlPrefix: '~/frontend-build/assets',
		dist: brand,
		authToken: config.authToken
	};
	return options;
};

export const initSentry = (apps: Array<App<Element>>) => {
	const sentrySettings = dataLayerSentrySettings();
	const dsn = sentrySettings.dsn;
	const environment = sentrySettings.environment;
	const release = sentrySettings.release;

	if (dsn && environment) {
		Sentry.init({
			app: apps,
			dsn,
			environment,
			release,

			integrations: [Sentry.browserTracingIntegration()],
			tracePropagationTargets: [JSON.parse(import.meta.env.VITE_SENTRY_TRACE_TARGETS)],
			tracesSampleRate: 1.0,

			beforeSend: (event, hint) => {
				// eslint-disable-next-line no-use-before-define
				if (breadcrumbsContainGtmTerms(event) || messageContainsGtmTerms(hint) || stackTraceContainsGtmJsFile(event)) {
					return null;
				}
				return event;
			}
		});
	}
};

const breadcrumbsContainGtmTerms = (event: ErrorEvent) => {
	if (!event.breadcrumbs) {
		return false;
	}
	return event.breadcrumbs.some(breadcrumb => breadcrumb.message &&
		(breadcrumb.message.includes('sgtm.caiway.nl') || breadcrumb.message.includes('sgtm.delta.nl')));
};

const messageContainsGtmTerms = (hint: Sentry.EventHint) => {
	const exception = hint.originalException as Error | null;

	if (exception && exception.message) {
		return exception.message.includes('gtag/js') || exception.message.includes('googletag') ||
			exception.message.includes('sgtm.caiway.nl') || exception.message.includes('sgtm.delta.nl');
	}
	return false;
};

const stackTraceContainsGtmJsFile = (event: ErrorEvent) => {
	if (!event.exception?.values) {
		return false;
	}
	for (const exception of event.exception.values) {
		if (!exception.stacktrace?.frames) {
			continue;
		}
		return exception.stacktrace.frames.some(frame => frame.filename?.includes('gtm.js'));
	}
	return false;
};

